import { heroKeywords } from '@/mocks/homeData';

interface HeroSectionProps {
  query: string;
  onQueryChange: (value: string) => void;
  onSearch: () => void;
}

export default function HeroSection({ query, onQueryChange, onSearch }: HeroSectionProps) {
  return (
    <section id="top" className="relative overflow-hidden border-b border-background-200 bg-background-50">
      <div className="pointer-events-none absolute -right-24 top-10 h-64 w-64 rounded-full bg-primary-100/50 blur-3xl"></div>
      <div className="pointer-events-none absolute -left-20 bottom-0 h-56 w-56 rounded-full bg-accent-100/40 blur-3xl"></div>

      <div className="relative mx-auto grid w-full max-w-[1200px] grid-cols-1 items-center gap-10 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14 lg:py-20">
        <div className="animate-rise">
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-accent-200 bg-accent-50 px-3 py-1 font-label text-xs text-accent-700">
              <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-accent-500"></span>
              证据链深扒专区
            </span>
            <span className="font-label text-xs text-foreground-500">当前档案编号 #HJ-0931</span>
          </div>

          <h1 className="mt-5 font-heading text-[38px] font-black leading-[1.08] tracking-tight text-foreground-950 md:text-[56px]">
            网爆黑料
            <span className="mt-2 block text-[22px] font-bold leading-snug text-foreground-700 md:text-[26px]">
              证据链深扒 · 资金往来与合同文件全曝光
            </span>
          </h1>

          <p className="mt-5 max-w-xl text-sm leading-7 text-foreground-600 md:text-[15px]">
            把散落在各处的材料收拢到一处：资金往来的每一笔流转、合同文件的每一处矛盾、内部邮件的每一条抄送、人物关系的每一根连线，按证据与时间两层归档，帮你理清事件脉络，看清真相，不放过任何细节。
          </p>

          <div className="mt-6 flex flex-wrap gap-2">
            {heroKeywords.map((keyword) => (
              <span
                key={keyword}
                className="rounded-md border border-background-200 bg-background-100 px-3 py-1.5 font-label text-xs text-foreground-700"
              >
                {keyword}
              </span>
            ))}
          </div>

          <div className="mt-8 flex w-full flex-col gap-3 sm:flex-row">
            <div className="flex flex-1 items-center gap-2 rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5">
              <i className="ri-search-line text-base leading-none text-foreground-400"></i>
              <input
                value={query}
                onChange={(e) => onQueryChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onSearch();
                }}
                placeholder="输入事件关键词或线索编号，检索证据链"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
            </div>
            <button
              type="button"
              onClick={onSearch}
              className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              检索线索
              <i className="ri-arrow-right-line text-base leading-none"></i>
            </button>
          </div>

          <dl className="mt-8 grid grid-cols-3 gap-4 border-t border-background-200 pt-6">
            <div>
              <dt className="font-label text-xs text-foreground-500">已归档线索</dt>
              <dd className="mt-1 font-heading text-xl font-bold text-foreground-950">1,286</dd>
            </div>
            <div>
              <dt className="font-label text-xs text-foreground-500">证据条目</dt>
              <dd className="mt-1 font-heading text-xl font-bold text-foreground-950">9,470</dd>
            </div>
            <div>
              <dt className="font-label text-xs text-foreground-500">时间跨度</dt>
              <dd className="mt-1 font-heading text-xl font-bold text-accent-600">19 个月</dd>
            </div>
          </dl>
        </div>

        <div className="animate-rise lg:pl-4">
          <div className="relative overflow-hidden rounded-lg border border-background-200 bg-background-100">
            <div className="h-[260px] w-full md:h-[380px] lg:h-[440px]">
              <img
                src="https://readdy.ai/api/search-image?query=Overhead%20flat%20lay%20of%20an%20investigation%20case%20file%20on%20a%20warm%20cream%20desk%2C%20scattered%20paper%20documents%2C%20contract%20pages%2C%20bank%20statement%20printouts%20and%20pinned%20index%20cards%20connected%20by%20thin%20red%20thread%20on%20a%20cork%20board%2C%20soft%20natural%20window%20light%2C%20muted%20amber%20and%20deep%20crimson%20accents%2C%20minimal%20clean%20composition%2C%20editorial%20documentary%20photography%2C%20high%20detail&width=1200&height=1000&seq=wangbao-hero-case-file-01&orientation=landscape"
                alt="网爆黑料证据链梳理桌面，合同文件与资金往来材料归档现场"
                title="网爆黑料证据链 · 资金往来与合同文件归档"
                className="h-full w-full object-top object-cover"
              />
            </div>
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-foreground-950/35 via-transparent to-transparent"></div>
            <div className="absolute left-4 right-4 bottom-4 rounded-lg border border-background-200 bg-background-50/95 p-4 backdrop-blur-sm">
              <div className="flex items-center justify-between">
                <span className="font-heading text-sm font-bold text-foreground-950">档案速览</span>
                <span className="flex items-center gap-1.5 font-label text-[11px] text-foreground-500">
                  <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-primary-500"></span>
                  更新于 09-08
                </span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2.5 sm:grid-cols-4">
                <div className="flex flex-col">
                  <span className="font-label text-[11px] text-foreground-500">资金往来</span>
                  <span className="font-heading text-sm font-bold text-foreground-950">3,215 笔</span>
                </div>
                <div className="flex flex-col">
                  <span className="font-label text-[11px] text-foreground-500">合同文件</span>
                  <span className="font-heading text-sm font-bold text-foreground-950">1,286 份</span>
                </div>
                <div className="flex flex-col">
                  <span className="font-label text-[11px] text-foreground-500">内部邮件</span>
                  <span className="font-heading text-sm font-bold text-foreground-950">4,702 封</span>
                </div>
                <div className="flex flex-col">
                  <span className="font-label text-[11px] text-foreground-500">关系节点</span>
                  <span className="font-heading text-sm font-bold text-accent-600">412 个</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}